/**
 * Consultancy / inventory desk — persisted in localStorage (same dev pattern as workspace).
 * No seed data: all arrays start empty until users record buys, sales, or (future) MIS imports.
 */
import { useCallback, useEffect, useState } from 'react';
import { loadDevJson, saveDevJson } from './devStorage';
import { consultancyApi, hasAuthToken } from '../api';
import { inRange, type DateRange } from './dateFilter';
import { handleSyncRejection } from './periodLockNotice';

export const BUNDLE_KEY = 'consultancy_bundle_v1';

export const CONSULTANCY_DATA_EVENT = 'consultancy-data';

function emit() {
  window.dispatchEvent(new Event(CONSULTANCY_DATA_EVENT));
}

export type ConsultancyPersistedVehicle = {
  id: string;
  name: string;
  model: string;
  year: number;
  status: 'available' | 'sold' | 'maintenance';
  /** Acquisition cost / amount invested in this unit. */
  price: number;
  /** Optional absolute or same-origin image URL (first photo, for cards) */
  imageUrl?: string;
  /** All uploaded photos (data URLs or URLs). */
  images?: string[];
  location?: string;
  kms?: number;
  /** When the stock was acquired (ISO). Drives investment-by-date. */
  boughtAt?: string;
  vehicleNumber?: string;
  sellerName?: string;
  chasisNumber?: string;
  engineNumber?: string;
  repairs?: string;
  cBookNumber?: string;
  dealRef?: string;
  /** How acquisition money left the shop (cash / bank / split). */
  paymentVia?: 'cash' | 'online' | 'split' | '';
  companyBankId?: string;
  companyBankLabel?: string;
  paidBy?: string;
  cashAmount?: number;
  bankAmount?: number;
};

export type ConsultancySaleRecord = Record<string, unknown> & {
  recordedAt: string;
  /** Stock line sold — required for new sales; older rows may omit. */
  vehicleId?: string;
  /** Snapshot for ledger / reports */
  vehicleName?: string;
  /** Acquisition cost (from stock card) at time of sale — for margin */
  acquisitionCostAtSale?: number;
  customerName?: string;
  sellingPrice?: string | number;
};

export type ConsultancyMonthlyAmount = { month: string; amount: number };
export type ConsultancyMonthlyProfit = { month: string; profit: number };
export type ConsultancyMonthlyLoss = { month: string; loss: number };
export type ConsultancyOverviewPoint = { name: string; profit: number; loss: number; revenue: number };
export type ConsultancyInvestmentBucket = { type: string; amount: number };
export type ConsultancyInvestmentChartRow = { name: string; investment: number; returns: number };

export type ConsultancyBundle = {
  vehicles: ConsultancyPersistedVehicle[];
  sales: ConsultancySaleRecord[];
  /** Manual / imported P&L lines (optional). */
  monthlyRevenue: ConsultancyMonthlyAmount[];
  monthlyProfit: ConsultancyMonthlyProfit[];
  monthlyLoss: ConsultancyMonthlyLoss[];
  overviewTrend: ConsultancyOverviewPoint[];
  investmentBuckets: ConsultancyInvestmentBucket[];
  investmentChart: ConsultancyInvestmentChartRow[];
  investmentTotals: { totalInvestment: number; returns: number; duration: string };
};

/**
 * Auto-computed consultancy economics from raw buys (vehicles) and sales:
 *   investment = acquisition cost of stock bought (optionally in date range)
 *   revenue    = selling price of sales (optionally in date range)
 *   profit     = revenue - cost-of-goods-sold
 * Available stock value is always current on-hand (not date-windowed).
 */
export function computeConsultancyEconomics(bundle: ConsultancyBundle, range?: DateRange | null) {
  const scoped = range && (range.start || range.end);
  const bought = scoped
    ? bundle.vehicles.filter((v) => inRange(v.boughtAt, range!))
    : bundle.vehicles;
  const sales = scoped
    ? bundle.sales.filter((s) => inRange(s.recordedAt, range!))
    : bundle.sales;

  const investment = bought.reduce((s, v) => s + (Number(v.price) || 0), 0);
  const availableValue = bundle.vehicles
    .filter((v) => v.status === 'available')
    .reduce((s, v) => s + (Number(v.price) || 0), 0);
  let revenue = 0;
  let cogs = 0;
  for (const sale of sales) {
    revenue += Math.max(0, parseFloat(String(sale.sellingPrice ?? '')) || 0);
    cogs += Math.max(0, Number(sale.acquisitionCostAtSale) || 0);
  }
  const grossProfit = revenue - cogs;
  return {
    investment,
    availableValue,
    revenue,
    cogs,
    grossProfit,
    profit: grossProfit > 0 ? grossProfit : 0,
    loss: grossProfit < 0 ? -grossProfit : 0,
    /** Sales / buys included after date filter (for badges). */
    saleCount: sales.length,
    buyCount: bought.length,
  };
}

/** Per-sale profit rows (selling price − acquisition cost). */
export function saleProfitRows(bundle: ConsultancyBundle, range?: DateRange | null) {
  const scoped = range && (range.start || range.end);
  const sales = scoped
    ? bundle.sales.filter((s) => inRange(s.recordedAt, range!))
    : bundle.sales;
  return sales.map((s) => {
    const sellingPrice = Math.max(0, parseFloat(String(s.sellingPrice ?? '')) || 0);
    const cost = Math.max(0, Number(s.acquisitionCostAtSale) || 0);
    return {
      recordedAt: String(s.recordedAt),
      vehicleName: String(s.vehicleName ?? '—'),
      customerName: String(s.customerName ?? '—'),
      sellingPrice,
      cost,
      profit: sellingPrice - cost,
    };
  });
}

/** Aggregate per-sale profit/loss into monthly profit & loss arrays. */
export function profitLossByMonth(
  bundle: ConsultancyBundle,
  range?: DateRange | null
): {
  monthlyProfit: ConsultancyMonthlyProfit[];
  monthlyLoss: ConsultancyMonthlyLoss[];
} {
  const profit = new Map<string, number>();
  const loss = new Map<string, number>();
  for (const r of saleProfitRows(bundle, range)) {
    const dt = new Date(r.recordedAt);
    if (!Number.isFinite(dt.getTime())) continue;
    const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
    if (r.profit >= 0) profit.set(key, (profit.get(key) ?? 0) + r.profit);
    else loss.set(key, (loss.get(key) ?? 0) + -r.profit);
  }
  return {
    monthlyProfit: [...profit.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([month, p]) => ({ month, profit: p })),
    monthlyLoss: [...loss.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([month, l]) => ({ month, loss: l })),
  };
}

export function emptyConsultancyBundle(): ConsultancyBundle {
  return {
    vehicles: [],
    sales: [],
    monthlyRevenue: [],
    monthlyProfit: [],
    monthlyLoss: [],
    overviewTrend: [],
    investmentBuckets: [],
    investmentChart: [],
    investmentTotals: { totalInvestment: 0, returns: 0, duration: '—' },
  };
}

/** Legacy default stamped on buys before KM/location cleanup — strip on read. */
function scrubLegacyVehicleLocation(v: ConsultancyPersistedVehicle): ConsultancyPersistedVehicle {
  const loc = String(v.location ?? '').trim();
  if (/^intake\s*·\s*pending\s*inspection$/i.test(loc)) {
    return { ...v, location: '' };
  }
  return v;
}

export function readConsultancyBundle(): ConsultancyBundle {
  const raw = loadDevJson<Partial<ConsultancyBundle> | null>(BUNDLE_KEY, null);
  if (!raw || typeof raw !== 'object') return emptyConsultancyBundle();
  const d = emptyConsultancyBundle();
  return {
    vehicles: Array.isArray(raw.vehicles)
      ? raw.vehicles.map((v) => scrubLegacyVehicleLocation(v as ConsultancyPersistedVehicle))
      : [],
    sales: Array.isArray(raw.sales) ? raw.sales : [],
    monthlyRevenue: Array.isArray(raw.monthlyRevenue) ? raw.monthlyRevenue : [],
    monthlyProfit: Array.isArray(raw.monthlyProfit) ? raw.monthlyProfit : [],
    monthlyLoss: Array.isArray(raw.monthlyLoss) ? raw.monthlyLoss : [],
    overviewTrend: Array.isArray(raw.overviewTrend) ? raw.overviewTrend : [],
    investmentBuckets: Array.isArray(raw.investmentBuckets) ? raw.investmentBuckets : [],
    investmentChart: Array.isArray(raw.investmentChart) ? raw.investmentChart : [],
    investmentTotals:
      raw.investmentTotals && typeof raw.investmentTotals === 'object'
        ? {
            totalInvestment: Math.max(0, Number((raw.investmentTotals as { totalInvestment?: number }).totalInvestment) || 0),
            returns: Math.max(0, Number((raw.investmentTotals as { returns?: number }).returns) || 0),
            duration: String((raw.investmentTotals as { duration?: string }).duration || '—'),
          }
        : d.investmentTotals,
  };
}

export function writeConsultancyBundle(next: ConsultancyBundle): void {
  saveDevJson(BUNDLE_KEY, next);
  if (hasAuthToken()) {
    consultancyApi.put(next as unknown as Record<string, unknown>).catch((e) => {
      // Closed-year rejections restore the server copy; otherwise keep local and retry on next change.
      handleSyncRejection(e);
    });
  }
  emit();
}

export function useConsultancyBundle(): readonly [ConsultancyBundle, (fn: (prev: ConsultancyBundle) => ConsultancyBundle) => void] {
  const [bundle, setBundle] = useState(() => readConsultancyBundle());

  useEffect(() => {
    const on = () => setBundle(readConsultancyBundle());
    window.addEventListener(CONSULTANCY_DATA_EVENT, on);
    return () => window.removeEventListener(CONSULTANCY_DATA_EVENT, on);
  }, []);

  const update = useCallback((fn: (prev: ConsultancyBundle) => ConsultancyBundle) => {
    const next = fn(readConsultancyBundle());
    writeConsultancyBundle(next);
    setBundle(next);
  }, []);

  return [bundle, update] as const;
}

/** Aggregate sale receipts by calendar month (YYYY-MM) for revenue views. */
export function revenueMonthsFromSales(sales: ConsultancySaleRecord[]): ConsultancyMonthlyAmount[] {
  const map = new Map<string, number>();
  for (const s of sales) {
    const dt = new Date(String(s.recordedAt));
    if (!Number.isFinite(dt.getTime())) continue;
    const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
    const amt = Math.max(0, parseFloat(String(s.sellingPrice ?? '')) || 0);
    map.set(key, (map.get(key) ?? 0) + amt);
  }
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, amount]) => ({ month, amount }));
}

export function mergeMonthlyRevenue(
  manual: ConsultancyMonthlyAmount[],
  sales: ConsultancySaleRecord[],
  range?: DateRange | null
): ConsultancyMonthlyAmount[] {
  const scoped = range && (range.start || range.end);
  const salesInRange = scoped ? sales.filter((s) => inRange(s.recordedAt, range!)) : sales;
  const m = new Map<string, number>();
  for (const x of manual) {
    if (scoped && !inRange(`${x.month}-01`, range!)) continue;
    m.set(x.month, (m.get(x.month) ?? 0) + Math.max(0, x.amount));
  }
  for (const x of revenueMonthsFromSales(salesInRange)) {
    m.set(x.month, (m.get(x.month) ?? 0) + x.amount);
  }
  return [...m.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, amount]) => ({ month, amount }));
}

export function buildOverviewTrend(
  revenue: ConsultancyMonthlyAmount[],
  profit: ConsultancyMonthlyProfit[],
  loss: ConsultancyMonthlyLoss[]
): ConsultancyOverviewPoint[] {
  const keys = new Set<string>();
  for (const r of revenue) keys.add(r.month);
  for (const p of profit) keys.add(p.month);
  for (const l of loss) keys.add(l.month);
  return [...keys].sort().map((name) => ({
    name,
    revenue: revenue.find((r) => r.month === name)?.amount ?? 0,
    profit: profit.find((p) => p.month === name)?.profit ?? 0,
    loss: loss.find((l) => l.month === name)?.loss ?? 0,
  }));
}
